<template>
	<FloatingPanel
		:title="labels.title"
		@close="close"
	>
		<template #subtitle>
			<p>
				<span v-html="labels.body" />
			</p>
		</template>
		<template #body>
			<div
				v-if="isTagTypeInputActive"
				class="form-group"
			>
				<FormField
					:label="labels.tagType"
				>
					<Dropdown
						v-model="tagType"
						:disabled="formDisabled"
						:is-small="true"
						:options="tagTypeOptions"
						class="w-full"
						data-qa="edit-multiple-tag-type-dropdown"
					/>
				</FormField>
			</div>
			<div
				v-if="isTeamFieldActive"
			>
				<div class="form-group">
					<FormField
						:label="labels.team"
					>
						<Dropdown
							v-model="team"
							:disabled="formDisabled"
							:is-small="true"
							:options="participantsOptions"
							class="w-full"
							data-qa="edit-multiple-team-dropdown"
						/>
					</FormField>
				</div>

				<div class="form-group">
					<FormField
						:label="labels.player"
					>
						<Dropdown
							v-model="player"
							:is-small="true"
							:options="playerOptions"
							class="w-full"
							:disabled="playerOptions.length === 0"
							data-qa="edit-multiple-player-dropdown"
						/>
					</FormField>
				</div>
			</div>
			<div
				v-else-if="isAthleteFieldActive"
				class="form-group"
			>
				<FormField
					:label="labels.athlete"
				>
					<Dropdown
						v-model="athlete"
						:is-small="true"
						:options="participantsOptions"
						class="w-full"
						:disabled="participantsOptions.length === 0"
						data-qa="edit-multiple-athlete-dropdown"
					/>
				</FormField>
			</div>
			<div
				v-if="isTimeShiftEnabled"
				class="timeshift-wrapper"
			>
				<div class="form-group">
					<div class="h-disp-flex mb-2">
						<span class="mr-2">{{ labels.timeShift.title }}</span>
						<LabelWithTooltip
							:label="transUcfirst('one.tag_timeshift_info')"
							:wrapper-class="null"
							:tooltip-class="`icon-tooltip-w200`"
						>
							<span class="h-cursor-pointer">
								<Icon
									icon-name="c-info"
									size="lg"
								/>
							</span>
						</LabelWithTooltip>
					</div>
					<div class="h-disp-flex">
						<button
							class="button button--switch h-width-full"
							:disabled="formDisabled"
							:class="{selected: isTimeShiftBackSelected}"
							@click="handleTimeShiftBackwardClick"
						>
							{{ trans('commons.back') }}
						</button>
						<button
							class="button button--switch h-width-full"
							:disabled="formDisabled"
							:class="{selected: isTimeShiftForthSelected}"
							@click="handleTimeShiftForthClick"
						>
							{{ trans('commons.forth') }}
						</button>
					</div>
				</div>
				<div class="form-group">
					<FormField
						:label="labels.timeShift.amount"
						:error="formErrors.timeShiftOffset"
					>
						<TimeInput
							v-model="timeShiftOffset"
							:is-valid="!formErrors.timeShiftOffset"
							:disabled="formDisabled"
						>
							<template #right>
								<Icon
									icon-name="clock"
									size="lg"
								/>
							</template>
						</TimeInput>
					</FormField>
				</div>
			</div>
		</template>

		<template
			v-if="!showConfirmation"
			#footer
		>
			<div class="form-actions">
				<DiscardButton
					:disabled="!hasChanges"
					@click.native="formReset"
				/>
				<button
					class="button button--primary"
					data-qa="save-tag-button"
					:disabled="!canSave"
					@click="handleSave"
				>
					{{ transUcfirst('commons.save') }}
				</button>
			</div>
		</template>

		<template
			v-if="showConfirmation"
			#footer-overlay
		>
			<div class="form-confirm-box">
				<p class="description">
					{{ transUcfirst('one.tag_timeshift_overflow_warning') }}
				</p>
				<div class="form-actions">
					<button
						class="button w-full button--secondary"
						@click="handleDiscard"
					>
						{{ transUcfirst('commons.cancel') }}
					</button>
					<button
						class="button w-full button--primary"
						@click="handleConfirmSave"
					>
						{{ transUcfirst('commons.save_anyway') }}
					</button>
				</div>
			</div>
		</template>
	</FloatingPanel>
</template>

<script>
import FloatingPanel from '../Panels/FloatingPanel';
import { mapActions, mapGetters } from 'vuex';
import FormField from '../Form/FormField';
import Dropdown from '@components/dropdown';
import DiscardButton from '@components/buttons/DiscardButton.vue';
import TimeInput from '@components/TimeInput/TimeInput';
import { TAG_NAME_CLOCK_STOP, TAG_NAME_CLOCK_SET, TAG_NAME_CLOCK_START } from '@videotools/js/api/entities/TagButton';
import { GAME_SPORT } from '@videotools/js/constants/game';

const TIME_SHIFT_DIR_BACK = 'back';
const TIME_SHIFT_DIR_FORTH = 'forth';

const SAVE_CONFIRMATION_NONE = 'none';
const SAVE_CONFIRMATION_REQUIRED = 'required';
const SAVE_CONFIRMATION_CONFIRMED = 'confirmed';

const CLOCK_TAGS = [
	TAG_NAME_CLOCK_STOP,
	TAG_NAME_CLOCK_SET,
	TAG_NAME_CLOCK_START,
];

export default {
	name:       'EventTagBulkEdit',
	components: { FormField, FloatingPanel, Dropdown, DiscardButton, TimeInput },
	props:      {
	},
	data() {
		return {
			tagType:            null,
			tagTypeInitial:	    null,
			team:               null,
			teamInitial:        null,
			player:             null,
			playerInitial:      null,
			athlete:            null,
			athleteInitial:     null,
			timeShiftDirection: TIME_SHIFT_DIR_BACK,
			timeShiftOffset:    0,
			saveConfirmStatus:  SAVE_CONFIRMATION_NONE,
			formErrors:         {
				timeShiftOffset: null,
			},
		};
	},
	computed: {
		...mapGetters({
			isAppleDevice:        'device/isAppleDevice',
			isVod:                'event/isVod',
			event:                'event/event',
			eventParticipants:    'event/participants',
			sportName:            'event/sportName',
			sportParticipantType: 'event/participantType',
			areAllTagsSelected:   'eventTagEdit/areAllTagsSelected',
			eventTags:            'eventTagEdit/selectedTags',
			isForEvent:           'tags/isForEvent',
			isForParticipant:     'tags/isForParticipant',
			isForTiming:          'tags/isForTiming',
			eventTargetTags:      'tags/eventTargetTags',
			participantTags:      'tags/participantTags',
			timingTags:           'tags/timingTags',
			isPlayerCompetition:  'event/isPlayerCompetition',
		}),
		labels() {
			return {
				title: this.$I18n.trans('commons.type_edit', {
					type: this.$I18n.trans_choice('one.tag', 2),
				}),
				body:      this.$I18n.trans('one.you_have_selected_count', { num: this.tagCount, type: this.$I18n.trans_choice('one.tag', this.tagCount) }),
				item:      this.$I18n.trans_choice('one.tag', 2),
				tagType:   this.$I18n.trans('one.tag_type'),
				team:      this.transChoiceUcFirst('commons.team', 1),
				player:    this.transChoiceUcFirst('commons.player', 1),
				athlete:   this.transChoiceUcFirst('commons.athlete', 1),
				timeShift: {
					title:  this.transUcfirst('one.tag_timeshift_title'),
					amount: this.transUcfirst('one.tag_timeshift_amount'),
				},
			};
		},
		timeShiftDirectionSign() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_FORTH ? 1 : -1;
		},
		tagCount() {
			return this.eventTags.length;
		},
		tagTypeOptions() {
			const mapper = (tag) => {
				return {
					key:   `tag_${tag.tag.id}`,
					id:    tag.tag.id,
					label: this.transTagLabel(tag.name, this.sportName, tag.tag.type),
					value: tag.tag,
				};
			};

			// determine if the selected event tags have different scopes (event, participant or timing)
			// and allow bulk editing of the tag type only if they all have the same scope
			// eg: cannot set the same type for a clock start tag (timing) and a goal tag (participant)
			if (this.tagScopes.length > 1) {
				return [];
			}

			const tagScope = this.tagScopes[0];
			if (tagScope === 'timing') {
				return this.timingTags.map(mapper);
			}
			if (tagScope === 'participant') {
				return this.participantTags.map(mapper);
			}
			if (tagScope === 'event') {
				return this.eventTargetTags.map(mapper);
			}

			return [];
		},
		tagScopes() {
			return this.eventTags.reduce(
				(acc, eventTag) => {
					let scope;
					const tag = eventTag.tag;
					if (this.isForParticipant(tag) || eventTag.playerId) { // ugly hack to also include 'cut' athlete tags within the `participant` scope
						scope = 'participant';
					} else if (this.isForTiming(tag)) {
						scope = 'timing';
					} else if (this.isForEvent(tag)) {
						scope = 'event';
					}
					if (!acc.includes(scope)) {
						return [...acc, scope];
					}
					return acc;
				},
				[]
			);
		},
		participantsOptions() {
			return this.eventParticipants.map((participant) => ({
				key:   `participant_${participant.id}`,
				id:    participant.id,
				label: participant.club ? participant.club.name : participant.name,
				value: participant,
			}));
		},
		playerOptions() {
			if (this.eventParticipants.length === 2 && this.team) {
				// populate participants only for the selected team if any
				const team = this.eventParticipants
					.find((participant) => participant.id === this.team.id);
				if (team && team.players) {
					return team.players.map((player) => ({
						key:   `player_${player.id}`,
						id:    player.id,
						label: player.name,
						value: player,
					}));
				}
			}
			return [];
		},
		isTimeShiftEnabled() {
			return true;
		},
		isTimeShiftBackSelected() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_BACK;
		},
		isTimeShiftForthSelected() {
			return this.timeShiftDirection === TIME_SHIFT_DIR_FORTH;
		},
		isTagTypeInputActive() {
			return this.tagTypeOptions.length;
		},
		isTeamFieldActive() {
			return !this.isPlayerCompetition && this.tagScopes.length === 1 && this.tagScopes[0] === 'participant';
		},
		isAthleteFieldActive() {
			return this.isPlayerCompetition && this.tagScopes.length === 1 && this.tagScopes[0] === 'participant';
		},
		hasFormErrors() {
			return Object.values(this.formErrors).find(err => !!err);
		},
		canSave() {
			return !this.hasFormErrors && (this.hasTagAttributesChanges || this.hasTimeShiftChanges);
		},
		hasChanges() {
			return this.hasTagAttributesChanges || this.hasTimeShiftChanges;
		},
		hasTagAttributesChanges() {
			const subjects = [
				[
					this.tagType ? this.tagType.id : null,
					this.tagTypeInitial ? this.tagTypeInitial.id : null,
				],
				[
					this.team ? this.team.id : null,
					this.teamInitial ? this.teamInitial.id : null,
				],
				[
					this.athlete ? this.athlete.id : null,
					this.athleteInitial ? this.athleteInitial.id : null,
				],
				[
					this.player ? this.player.id : null,
					this.playerInitial ? this.playerInitial.id : null,
				],
			];

			return !subjects.every(pairs => {
				return pairs[0] === pairs[1];
			});
		},
		hasTimeShiftChanges() {
			return !!this.timeShiftOffset;
		},
		showConfirmation() {
			// return true;
			return this.saveConfirmStatus === SAVE_CONFIRMATION_REQUIRED;
		},
		formDisabled() {
			return this.showConfirmation ||
				(
					[GAME_SPORT.ICE_HOCKEY, GAME_SPORT.FLOORBALL].includes(this.sportName) &&
					this.eventTags.some((eventTag) => CLOCK_TAGS.includes(eventTag.tag.name))
				);
		},
		footerClasses() {
			return this.showConfirmation ? 'form-confirmation-ct' : '';
		},
	},
	watch: {
		tags() {
			this.init();
		},
		timeShiftOffset(val) {
			this.validateTimeShiftOffset(val);
		},
	},
	mounted() {
		this.init();
	},
	methods: {
		...mapActions('eventTagEdit', [
			'editEventTag',
			'bulkEditEventTags',
			'bulkSave',
			'clearSelection',
		]),
		close() {
			this.clearSelection();
		},
		init() {
			// check same value, set inside
			const tagsHaveTheSamePlayer =
					this.eventTags.length > 1 &&
					this.eventTags.every(
						(tag) => tag.player && tag.player.id === this.eventTags[0].player.id
					);

			const tagsHaveTheSameType =
					this.eventTags.length > 1 &&
					this.eventTags.every((tag) => tag.tag && tag.tag.id === this.eventTags[0].tag.id);

			const tagsHaveTheSameTeam =
					this.eventTags.length > 1 &&
					this.eventTags.every(
						(tag) =>
							tag.team &&
							tag.team.id === ((this.team || {}).id || this.eventTags[0].team.id)
					);

			if (tagsHaveTheSameType) {
				this.tagType = this.tagTypeInitial = this.eventTags[0].tag;
			}
			if (!this.isPlayerCompetition) {
				if (tagsHaveTheSameTeam) {
					this.team = this.teamInitial = this.eventTags[0].team;
				}

				if (tagsHaveTheSamePlayer) {
					this.player = this.playerInitial = this.eventTags[0].player;
				}
			} else if (tagsHaveTheSamePlayer) {
				this.athlete = this.athleteInitial = this.eventTags[0].player;
			}
		},
		handleCancel() {
			this.clearSelection();
		},
		handleSave() {
			if (this.saveConfirmStatus !== SAVE_CONFIRMATION_CONFIRMED && !this.validateTimeShiftOverflow()) {
				this.saveConfirmStatus = SAVE_CONFIRMATION_REQUIRED;
				return;
			}

			const data = {
				tagUpdates:   null,
				timeShift:    null,
				selectedTags: this.eventTags,
			};

			if (this.hasTagAttributesChanges) {
				if (!this.isPlayerCompetition) {
					data.tagUpdates = {
						tags:    this.eventTags,
						tagType: this.tagType,
						team:    this.team,
						player:  this.player,
						isIos:   this.isAppleDevice,
					};
				} else {
					data.tagUpdates = {
						tags:    this.eventTags,
						tagType: this.tagType,
						player:  this.athlete,
						isIos:   this.isAppleDevice,
					};
				}
			}

			if (this.hasTimeShiftChanges) {
				data.timeShift = {
					offset: this.timeShiftOffset * this.timeShiftDirectionSign,
				};
			}

			this.bulkSave(data);
			this.saveConfirmStatus = SAVE_CONFIRMATION_NONE;
		},
		handleDiscard() {
			this.saveConfirmStatus = SAVE_CONFIRMATION_NONE;
		},
		handleConfirmSave() {
			this.saveConfirmStatus = SAVE_CONFIRMATION_CONFIRMED;
			this.handleSave();
		},
		handleTimeShiftBackwardClick() {
			this.timeShiftDirection = TIME_SHIFT_DIR_BACK;
		},
		handleTimeShiftForthClick() {
			this.timeShiftDirection = TIME_SHIFT_DIR_FORTH;
		},
		validateTimeShiftOffset(value) {
			let err = null;
			if (!value) {
				err = this.trans('validation.time_format');
			}
			this.formErrors.timeShiftOffset = err;

			return !!err;
		},
		validateTimeShiftOverflow() {
			if (!this.eventTags.length) {
				return true;
			}

			// sort the tags in ascending order by timestamp
			const orderedTags = this.eventTags.sort((a, b) => a.timestamp === b.timestamp ? 0 : (a.timestamp < b.timestamp ? 1 : -1));
			const offset = this.timeShiftOffset * this.timeShiftDirectionSign;

			// check if any of the tags are outside the bounds of the event
			const invalidTag = [
				orderedTags[0],
				orderedTags[orderedTags.length - 1],
			].find((tag) => {
				return offset + tag.endTimestamp > this.event.duration ||
								offset + tag.startTimestamp < 0;
			});

			return !invalidTag;
		},
	},
};
</script>

<style lang="scss" scoped>

.timeshift-wrapper {
	margin-top: 48px;
}

</style>
